import {
    useContext,
    useEffect,
    useState,
    useMemo,
    type FC,
    useRef
} from "react";
import {
    type PanResponderGestureState,
    type AccessibilityActionEvent,
    type GestureResponderEvent,
    PanResponder,
    Pressable,
    Platform,
    View
} from "react-native";
import type IResizableHandleProps from "./type";
import type {
    ResizableHandleKeyboardEventType,
    ResizableHandleWebPropsType
} from "./type";
import stylesheet, {
    RESIZABLE_HANDLE_GRIP_DOTS,
    getHandleColors,
    getHandleHit,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    INTERACTION_STATE_DATA_KEY,
    getInteractionStateName,
    useInteractionState
} from "../../../../helpers/interaction";
import {
    useReducedMotion
} from "../../../../helpers/motion";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    getHorizontalStep,
    getDirectionSign
} from "../../../../helpers/direction";
import {
    ResizableIndexContext,
    ResizableContext
} from "../../context";
import {
    warnOnce
} from "../../../../utils/development";
import {
    RESIZABLE_KEYBOARD_STEP
} from "../../util";

const ResizableHandle: FC<IResizableHandleProps> = ({
    isGripVisible: isGripVisibleProp = false,
    keyboardStep = RESIZABLE_KEYBOARD_STEP,
    customTheme: customThemeProp,
    isDisabled = false,
    onHoverOut,
    onHoverIn,
    onFocus,
    onBlur,
    style,
    title,
    ...props
}) => {
    const context = useContext(ResizableContext);

    const index = useContext(ResizableIndexContext);

    const {
        shapeConfigs,
        touchTargets,
        shadowAlpha,
        elevations,
        durations,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customThemeProp ?? context?.customTheme);

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const isReduceMotionEnabled = useReducedMotion();

    const pointerType = usePointerType();

    const [
        isHoverVisible,
        setIsHoverVisible
    ] = useState(false);

    const [
        isDragging,
        setIsDragging
    ] = useState(false);

    const pageLockRef = useRef< |
    {
        userSelect: string;
        cursor: string;
    } |
    undefined>(undefined);

    const lastTapRef = useRef(0);

    const latestRef = useRef({
        isDisabled,
        direction,
        context,
        index
    });

    latestRef.current = {
        isDisabled,
        direction,
        context,
        index
    };

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered
    } = useInteractionState({
        isDisabled,
        onHoverOut,
        onHoverIn,
        onFocus,
        onBlur
    });

    const orientation = context?.orientation ?? "horizontal";

    const handleState = context?.getHandleState(index);

    const isHover = isHoverVisible && !isDisabled;

    const isGripVisible = isGripVisibleProp || pointerType === "coarse";

    const hit = getHandleHit({
        touchTargets,
        pointerType
    });

    const currentColors = getHandleColors({
        isDisabled,
        isDragging,
        isHover,
        colors
    });

    const {
        container: containerDynamicStyle,
        gripDot: gripDotDynamicStyle,
        gripRow: gripRowDynamicStyle,
        line: lineDynamicStyle,
        grip: gripDynamicStyle
    } = useStyles({
        limit: isDisabled ? undefined : handleState?.limit,
        isReduceMotionEnabled,
        isFocusVisible,
        currentColors,
        isGripVisible,
        shapeConfigs,
        orientation,
        shadowAlpha,
        elevations,
        isDisabled,
        isDragging,
        direction,
        durations,
        iconSizes,
        radiuses,
        borders,
        isHover,
        colors,
        spaces,
        hit
    });

    const handleUnlockPage = () => {
        const lock = pageLockRef.current;

        pageLockRef.current = undefined;

        if(!lock || Platform.OS !== "web") {
            return;
        }

        const body = globalThis.document?.body;

        if(!body) {
            return;
        }

        body.style.cursor = lock.cursor;

        body.style.userSelect = lock.userSelect;
    };

    const handleLockPage = () => {
        const body = Platform.OS === "web" ? globalThis.document?.body : undefined;

        if(!body) {
            return;
        }

        pageLockRef.current = {
            userSelect: body.style.userSelect,
            cursor: body.style.cursor
        };

        body.style.cursor = latestRef.current.context?.orientation === "vertical" ? "row-resize" : "col-resize";

        body.style.userSelect = "none";
    };

    const getDelta = (gestureState: PanResponderGestureState): number => {
        if(latestRef.current.context?.orientation === "vertical") {
            return gestureState.dy;
        }

        return gestureState.dx * getDirectionSign({
            direction: latestRef.current.direction
        });
    };

    const panResponder = useMemo(() => {
        const handleFinish = (gestureState: PanResponderGestureState, isRelease: boolean) => {
            const {
                context: currentContext,
                index: currentIndex
            } = latestRef.current;

            handleUnlockPage();

            setIsDragging(false);

            if(!currentContext) {
                return;
            }

            const isTap = isRelease && Math.abs(gestureState.dx) + Math.abs(gestureState.dy) < shapeConfigs.tapSlop;

            currentContext.handleDragEnd({
                delta: isTap || !isRelease ? 0 : getDelta(gestureState),
                index: currentIndex
            });

            if(!isTap) {
                lastTapRef.current = 0;

                return;
            }

            const now = Date.now();

            if(now - lastTapRef.current < durations.doubleTap) {
                lastTapRef.current = 0;

                currentContext.handleReset(currentIndex);

                return;
            }

            lastTapRef.current = now;
        };

        const isEnabled = () => !latestRef.current.isDisabled && !!latestRef.current.context;

        return PanResponder.create({
            onPanResponderTerminate: (_event: GestureResponderEvent, gestureState: PanResponderGestureState) => handleFinish(gestureState, false),
            onPanResponderRelease: (_event: GestureResponderEvent, gestureState: PanResponderGestureState) => handleFinish(gestureState, true),
            onPanResponderMove: (_event: GestureResponderEvent, gestureState: PanResponderGestureState) => {
                latestRef.current.context?.handleDragMove({
                    delta: getDelta(gestureState),
                    index: latestRef.current.index
                });
            },
            onPanResponderGrant: () => {
                latestRef.current.context?.handleDragStart(latestRef.current.index);

                handleLockPage();

                setIsDragging(true);
            },
            onPanResponderTerminationRequest: () => false,
            onMoveShouldSetPanResponder: isEnabled,
            onStartShouldSetPanResponder: isEnabled
        });
    }, [
        shapeConfigs.tapSlop,
        durations.doubleTap
    ]);

    useEffect(() => {
        if(!isHovered || isDisabled || pointerType === "coarse") {
            setIsHoverVisible(false);

            return;
        }

        const timer = setTimeout(() => {
            setIsHoverVisible(true);
        }, durations.hoverIntent);

        return () => {
            clearTimeout(timer);
        };
    }, [
        durations.hoverIntent,
        pointerType,
        isDisabled,
        isHovered
    ]);

    useEffect(() => {
        return () => {
            handleUnlockPage();
        };
    }, []);

    useEffect(() => {
        if(context) {
            return;
        }

        warnOnce({
            message: "Resizable.Handle: a handle must be placed between two panels inside a \"Resizable.PanelGroup\"."
        });
    }, [
        context
    ]);

    const onKeyDown = (event: ResizableHandleKeyboardEventType) => {
        if(!context || isDisabled || event.target !== event.currentTarget || event.altKey || event.ctrlKey || event.metaKey) {
            return;
        }

        if(event.key === "Enter") {
            event.preventDefault();

            context.handleToggleCollapse(index);

            return;
        }

        if(event.key === "Home" || event.key === "End") {
            event.preventDefault();

            context.handleEdge({
                edge: event.key === "Home" ? "start" : "end",
                index
            });

            return;
        }

        const verticalStep = event.key === "ArrowDown" ? 1 : event.key === "ArrowUp" ? -1 : 0;

        const step = orientation === "horizontal" ? getHorizontalStep({
            key: event.key,
            direction
        }) : verticalStep;

        if(step === 0) {
            return;
        }

        event.preventDefault();

        context.handleStep({
            isShift: !!event.shiftKey,
            direction: step,
            keyboardStep,
            index
        });
    };

    const onAccessibilityAction = (event: AccessibilityActionEvent) => {
        if(!context || isDisabled) {
            return;
        }

        context.handleStep({
            direction: event.nativeEvent.actionName === "increment" ? 1 : -1,
            isShift: false,
            keyboardStep,
            index
        });
    };

    const webProps: ResizableHandleWebPropsType = {};

    if(handleState) {
        webProps["aria-orientation"] = orientation === "horizontal" ? "vertical" : "horizontal";
    }

    if(handleState && !isDisabled) {
        webProps["aria-valuetext"] = handleState.valueText;

        webProps["aria-controls"] = handleState.controls;

        webProps["aria-valuemax"] = handleState.valueMax;

        webProps["aria-valuemin"] = handleState.valueMin;

        webProps["aria-valuenow"] = handleState.valueNow;

        webProps.onKeyDown = onKeyDown;
    }

    const dataSet = interactionHandlers.dataSet ? {
        [INTERACTION_STATE_DATA_KEY]: getInteractionStateName({
            isPressed: isDragging,
            isHovered: isHover,
            isFocusVisible,
            isDisabled
        })
    } : undefined;

    const stateProps = {
        dataSet
    };

    const renderGrip = () => {
        if(!isGripVisible) {
            return null;
        }

        return <View
            style={[
                stylesheet.grip,
                gripDynamicStyle
            ]}
        >
            {
                RESIZABLE_HANDLE_GRIP_DOTS[orientation].map((row) => <View
                    style={[
                        stylesheet.gripRow,
                        gripRowDynamicStyle
                    ]}
                    key={row.join("-")}
                >
                    {
                        row.map((dot) => <View
                            style={gripDotDynamicStyle}
                            key={dot}
                        />)
                    }
                </View>)
            }
        </View>;
    };

    return <Pressable
        {...props}
        {...interactionHandlers}
        {...stateProps}
        {...webProps}
        aria-label={props["aria-label"] ?? title ?? handleState?.label}
        accessibilityActions={handleState && !isDisabled ? [
            {
                name: "increment"
            },
            {
                name: "decrement"
            }
        ] : undefined}
        onAccessibilityAction={onAccessibilityAction}
        aria-disabled={isDisabled}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        focusable={!isDisabled}
        disabled={isDisabled}
        role="separator"
    >
        <View
            style={[
                orientation === "horizontal" ? stylesheet.lineHorizontal : stylesheet.lineVertical,
                lineDynamicStyle
            ]}
        />
        {renderGrip()}
        {
            isDisabled ?
                null
            :
                <View
                    {...panResponder.panHandlers}
                    style={stylesheet.pan}
                />
        }
    </Pressable>;
};
export default Object.assign(ResizableHandle, {
    resizablePart: "handle" as const
});
